/**
 * TaskHost — mounted once at the app root, on every route.
 *
 *   ?task=<id>    opens TaskQuickView (a `tasks` doc: details, approve / return)
 *   ?dtask=<id>   opens the daily / team task popup (DailyTaskPopup)
 *   smc:openTask      event → same as ?task=
 *   smc:completeTask  event → the submit-for-review popup (TaskCompleteModal)
 *   smc:navigate      event / service-worker message → client-side navigation
 *                     (push-notification taps)
 *
 * Notifications link to /?task=<id> so a click lands on the exact task,
 * whatever tabs the member's role has.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import TaskQuickView from './TaskQuickView';
import TaskCompleteModal from '../TaskCompleteModal/TaskCompleteModal';
import { DailyTaskPopup } from '../../pages/DailyTasksPage';

function useSignedInMember() {
  const [user, setUser] = useState(null);
  useEffect(() => onAuthStateChanged(getAuth(getApp()), async fbUser => {
    if (!fbUser) { setUser(null); return; }
    let name = fbUser.displayName || fbUser.email || '';
    try {
      const snap = await getDoc(doc(getFirestore(getApp()), 'authorized_users', fbUser.uid));
      if (snap.exists()) name = snap.data().name || name;
    } catch (_) { /* name falls back to the auth profile */ }
    setUser({ uid: fbUser.uid, name });
  }), []);
  return user;
}

export default function TaskHost() {
  const user = useSignedInMember();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [completing, setCompleting] = useState(null);
  const taskId = params.get('task');
  const dtaskId = params.get('dtask');

  const clearParam = useCallback(key => setParams(prev => {
    const next = new URLSearchParams(prev);
    next.delete(key);
    return next;
  }, { replace: true }), [setParams]);

  useEffect(() => {
    const onOpen = e => {
      const id = e.detail?.taskId;
      if (id) setParams(prev => { const next = new URLSearchParams(prev); next.set('task', id); return next; });
    };
    const onComplete = async e => {
      const id = e.detail?.taskId;
      if (!id) return;
      try {
        const snap = await getDoc(doc(getFirestore(getApp()), 'tasks', id));
        if (snap.exists()) setCompleting({ id: snap.id, ...snap.data() });
      } catch (_) { /* no access — nothing to open */ }
    };
    const onNavigate = e => {
      const path = e.detail?.path || e.data?.path;
      if ((e.type === 'smc:navigate' || e.data?.type === 'smc:navigate') &&
        typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) navigate(path);
    };
    window.addEventListener('smc:openTask', onOpen);
    window.addEventListener('smc:completeTask', onComplete);
    window.addEventListener('smc:navigate', onNavigate);
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
    sw?.addEventListener('message', onNavigate);
    return () => {
      window.removeEventListener('smc:openTask', onOpen);
      window.removeEventListener('smc:completeTask', onComplete);
      window.removeEventListener('smc:navigate', onNavigate);
      sw?.removeEventListener('message', onNavigate);
    };
  }, [navigate, setParams]);

  if (!user) return null;
  return (
    <>
      {taskId && <TaskQuickView key={taskId} taskId={taskId} user={user} onClose={() => clearParam('task')} />}
      {dtaskId && <DailyTaskPopup key={dtaskId} taskId={dtaskId} user={user} onClose={() => clearParam('dtask')} />}
      {completing && (
        <TaskCompleteModal task={completing} userId={user.uid} userName={user.name}
          onClose={() => setCompleting(null)} onCompleted={() => setCompleting(null)} />
      )}
    </>
  );
}
